import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useMatchDetails } from '../../../shared/hooks/useMatchDetails';
import { useLineups } from '../../../shared/hooks/useLineups';
import { UILineupPlayer, MatchFormation } from '../../../shared/types/database';
import Toast from '../../../shared/components/ui/Toast';
import { supabase } from '../../../shared/lib/supabase';

// Reusable loading component for consistency
const TabLoader: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex items-center justify-center py-8">
    <div className="flex items-center space-x-2">
      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      <span className="text-sm text-gray-600">{message}</span>
    </div>
  </div>
);

const LINEUP_SCREENSHOT_BUCKET = 'lineup-screenshots';

const uploadLineupScreenshot = async (file: File, matchId: string, playerId: string) => {
  const sanitizedName = file.name.replace(/\s+/g, '-');
  const filePath = `${matchId}/${playerId}/${Date.now()}-${sanitizedName}`;

  const { error } = await supabase.storage
    .from(LINEUP_SCREENSHOT_BUCKET)
    .upload(filePath, file, { contentType: file.type, upsert: true });

  if (error) {
    throw error;
  }

  const { data } = supabase.storage.from(LINEUP_SCREENSHOT_BUCKET).getPublicUrl(filePath);
  return data?.publicUrl || null;
};

interface LineupTeamPanelProps {
  teamName: string;
  teamType: 'A' | 'B';
  teamId: string;
  teamLogo?: string;
  squad: UILineupPlayer[];
  formation: MatchFormation | null;
  matchStatus?: 'not_started' | 'live' | 'half_time' | 'full_time';
  onPlayerUpdate: (playerId: string, updates: Partial<UILineupPlayer>) => void;
  onScreenshotFileSelect: (playerId: string, file: File | null) => void;
  getPendingScreenshotUrl: (playerId: string) => string | null;
  onFormationUpdate: (formation: string, managerName?: string) => void;
}

interface PlayerCardProps {
  player: UILineupPlayer;
  onUpdate: (updates: Partial<UILineupPlayer>) => void;
  onScreenshotFileSelect?: (file: File | null) => void;
  pendingScreenshotUrl?: string | null;
  showActionButtons?: boolean;
  showCaptainBadge?: boolean;
  showRemoveOption?: boolean;
  compact?: boolean;
  matchStatus?: 'not_started' | 'live' | 'half_time' | 'full_time';
}

const PlayerCard: React.FC<PlayerCardProps> = ({ 
  player, 
  onUpdate, 
  onScreenshotFileSelect,
  pendingScreenshotUrl,
  showActionButtons = false,
  showCaptainBadge = false,
  showRemoveOption = false,
  compact = false,
  matchStatus = 'not_started'
}) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopyUrl = () => {
    if (player.identification_screenshot_url) {
      navigator.clipboard.writeText(player.identification_screenshot_url);
      setCopySuccess(true);
      window.setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleStartingToggle = () => {
    onUpdate({ 
      isStarting: !player.isStarting, 
      isBench: player.isStarting ? false : player.isBench 
    });
  };

  const handleBenchToggle = () => {
    onUpdate({ 
      isBench: !player.isBench, 
      isStarting: player.isBench ? false : player.isStarting 
    });
  };

  const handleCaptainToggle = () => {
    onUpdate({ isCaptain: !player.isCaptain });
  };

  const handleRemove = () => {
    onUpdate({ isStarting: false, isBench: false });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onScreenshotFileSelect) {
      onScreenshotFileSelect(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (compact) {
    const showIdentificationFields = player.isStarting || player.isBench;

    return (
      <div className="p-2 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors space-y-2">
        <div className="flex items-center justify-between">
          {/* Player Info */}
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            {/* Player Avatar */}
            <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
              {player.player?.profile_image ? (
                <img 
                  src={player.player.profile_image} 
                  alt={player.player.full_name} 
                  className="w-full h-full object-cover" 
                />
              ) : (
                <span className="text-blue-600 font-bold text-sm">
                  {player.player?.full_name?.charAt(0) || '?'}
                </span>
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <h4 className="text-sm font-semibold text-gray-800 truncate">
                  {player.player?.full_name || 'Unknown Player'}
                </h4>
              </div>
              <div className="flex items-center space-x-2 text-xs text-gray-600">
                {/* Kit Number Input */}
                <input
                  type="number"
                  value={player.kit_number || ''}
                  onChange={(e) => onUpdate({ kit_number: parseInt(e.target.value, 10) || undefined })}
                  className="w-12 bg-gray-100 px-1.5 py-0.5 rounded text-gray-700 font-medium text-center border-0 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  placeholder="#"
                  min="1"
                  max="99"
                />
                
                {/* Position Dropdown */}
                <select
                  value={player.position || ''}
                  onChange={(e) => onUpdate({ position: e.target.value as 'Goalkeeper' | 'Defender' | 'Midfielder' | 'Forward' })}
                  className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-700 font-medium border-0 focus:ring-1 focus:ring-blue-500 focus:outline-none text-xs"
                >
                  <option value="">Position</option>
                  <option value="Goalkeeper">Goalkeeper</option>
                  <option value="Defender">Defender</option>
                  <option value="Midfielder">Midfielder</option>
                  <option value="Forward">Forward</option>
                </select>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-2">
            {/* Captain Toggle (only for starting players) */}
            {showCaptainBadge && player.isStarting && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCaptainToggle();
                }}
                className={`w-6 h-6 rounded-full text-xs font-bold transition-colors ${
                  player.isCaptain
                    ? 'bg-yellow-500 text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-yellow-100'
                }`}
                title={player.isCaptain ? 'Remove Captain' : 'Make Captain'}
              >
                <span className="text-xs">C</span>
              </button>
            )}

            {/* Remove Option */}
            {showRemoveOption && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
                className="w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center hover:bg-red-200 transition-colors"
                title="Remove from lineup"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {showIdentificationFields && (
          <div className="space-y-2">
            <input
              type="text"
              value={player.identification_description || ''}
              onChange={(e) => onUpdate({ identification_description: e.target.value })}
              placeholder="Identification description"
              className="w-full px-2 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />

            <div className="space-y-1">
              {pendingScreenshotUrl ? (
                <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                  <img
                    src={pendingScreenshotUrl}
                    alt="Selected"
                    className="w-10 h-10 rounded object-cover border border-gray-200"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-blue-800 block">Image selected – will upload on Save</span>
                    <button
                      type="button"
                      onClick={() => onScreenshotFileSelect?.(null)}
                      className="text-xs text-red-600 hover:underline mt-0.5"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {player.identification_screenshot_url ? (
                    <div className="flex items-start gap-2 p-2 bg-gray-50 border border-gray-200 rounded-md">
                      <img
                        src={player.identification_screenshot_url}
                        alt={`${player.player?.full_name || 'Player'} screenshot`}
                        className="w-10 h-10 rounded object-cover border border-gray-200 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            readOnly
                            value={player.identification_screenshot_url}
                            className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded-md bg-white truncate"
                            title={player.identification_screenshot_url}
                          />
                          <button
                            type="button"
                            onClick={handleCopyUrl}
                            className="px-2 py-1 text-xs rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                          >
                            {copySuccess ? 'Copied!' : 'Copy URL'}
                          </button>
                        </div>
                        <label className="inline-block px-2 py-1 text-xs rounded-md border bg-amber-50 text-amber-800 border-amber-200 cursor-pointer">
                          Replace
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleFileSelect}
                          />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={player.identification_screenshot_url || ''}
                        onChange={(e) => onUpdate({ identification_screenshot_url: e.target.value })}
                        placeholder="Screenshot URL (or choose file below)"
                        className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                      <label className="px-2 py-1 text-xs rounded-md border bg-blue-50 text-blue-700 border-blue-200 cursor-pointer whitespace-nowrap">
                        Choose file
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleFileSelect}
                        />
                      </label>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Full card for available squad
  return (
    <div className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
      <div className="p-3">
        {/* Player Info */}
        <div className="flex items-center space-x-3 mb-3">
          {/* Player Avatar */}
          <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center overflow-hidden">
            {player.player?.profile_image ? (
              <img 
                src={player.player.profile_image} 
                alt={player.player.full_name} 
                className="w-full h-full object-cover" 
              />
            ) : (
              <span className="text-blue-600 font-bold text-lg">
                {player.player?.full_name?.charAt(0) || '?'}
              </span>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-gray-900 truncate">
              {player.player?.full_name || 'Unknown Player'}
            </h4>
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              {player.kit_number && (
                <span className="bg-gray-100 px-2 py-1 rounded">#{player.kit_number}</span>
              )}
              {player.position && (
                <span>{player.position}</span>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        {showActionButtons && (
          <div className="flex space-x-2">
            {/* Starting Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleStartingToggle();
              }}
              className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                player.isStarting
                  ? 'bg-green-100 text-green-800 border border-green-300'
                  : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-green-50 hover:text-green-700'
              }`}
            >
              Starting
            </button>

            {/* Bench Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleBenchToggle();
              }}
              className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                player.isBench
                  ? 'bg-orange-100 text-orange-800 border border-orange-300'
                  : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-orange-50 hover:text-orange-700'
              }`}
            >
              Bench
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const LineupTeamPanel: React.FC<LineupTeamPanelProps> = ({ 
  teamName, 
  teamType, 
  teamId,
  teamLogo,
  squad, 
  formation, 
  matchStatus = 'not_started',
  onPlayerUpdate, 
  onScreenshotFileSelect,
  getPendingScreenshotUrl,
  onFormationUpdate
}) => {
  const [managerName, setManagerName] = useState(formation?.manager_name || '');
  const [formationValue, setFormationValue] = useState(formation?.formation || '4-3-3');

  const handleManagerChange = (value: string) => {
    setManagerName(value);
    onFormationUpdate(formationValue, value);
  };

  const handleFormationChange = (value: string) => {
    setFormationValue(value);
    onFormationUpdate(value, managerName);
  };


  const startingPlayers = squad.filter(player => player.isStarting);
  const benchPlayers = squad.filter(player => player.isBench);
  const availablePlayers = squad.filter(player => !player.isStarting && !player.isBench);
  
  // Check if any player is already captain
  const hasCaptain = squad.some(player => player.isCaptain);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex-1 min-w-0">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center overflow-hidden">
            {teamLogo ? (
              <img 
                src={teamLogo} 
                alt={`${teamName} logo`} 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <span className={`text-gray-600 text-xs font-bold ${teamLogo ? 'hidden' : ''}`}>
              {teamName.charAt(0)}
            </span>
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-800">{teamName}</h3>
          </div>
        </div>
        
      </div>

      <div className="p-4 space-y-4">
        {/* Manager and Formation */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Manager</label>
            <input
              type="text"
              value={managerName}
              onChange={(e) => handleManagerChange(e.target.value)}
              placeholder="Manager Name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Formation</label>
            <input
              type="text"
              value={formationValue}
              onChange={(e) => handleFormationChange(e.target.value)}
              placeholder="4-3-3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
        </div>

        {/* Starting Lineup */}
        <div>
          <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
            <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
            Starting Lineup ({startingPlayers.length})
          </h4>
          <div className="space-y-1">
            {startingPlayers.length === 0 ? (
              <div className="text-center py-6 text-gray-500 text-sm bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                No starting lineup selected
              </div>
            ) : (
              startingPlayers.map((player) => (
                <PlayerCard
                  key={player.player_id}
                  player={player}
                  onUpdate={(updates) => onPlayerUpdate(player.player_id, updates)}
                  onScreenshotFileSelect={(file) => onScreenshotFileSelect(player.player_id, file)}
                  pendingScreenshotUrl={getPendingScreenshotUrl(player.player_id)}
                  compact={true}
                  showCaptainBadge={!hasCaptain || player.isCaptain}
                  showRemoveOption={true}
                  matchStatus={matchStatus}
                />
              ))
            )}
          </div>
        </div>

        {/* On Bench */}
        <div>
          <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
            <span className="w-3 h-3 bg-orange-500 rounded-full mr-2"></span>
            On Bench ({benchPlayers.length})
          </h4>
          <div className="space-y-1">
            {benchPlayers.length === 0 ? (
              <div className="text-center py-6 text-gray-500 text-sm bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                No bench players selected
              </div>
            ) : (
              benchPlayers.map((player) => (
                <PlayerCard
                  key={player.player_id}
                  player={player}
                  onUpdate={(updates) => onPlayerUpdate(player.player_id, updates)}
                  onScreenshotFileSelect={(file) => onScreenshotFileSelect(player.player_id, file)}
                  pendingScreenshotUrl={getPendingScreenshotUrl(player.player_id)}
                  compact={true}
                  showRemoveOption={true}
                  matchStatus={matchStatus}
                />
              ))
            )}
          </div>
        </div>

        {/* Available Squad */}
        <div>
          <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
            <span className="w-3 h-3 bg-gray-400 rounded-full mr-2"></span>
            Available Squad ({availablePlayers.length})
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {availablePlayers.length === 0 ? (
              <div className="col-span-2 text-center py-6 text-gray-500 text-sm bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                All players assigned
              </div>
            ) : (
              availablePlayers.map((player) => (
                <PlayerCard
                  key={player.player_id}
                  player={player}
                  onUpdate={(updates) => onPlayerUpdate(player.player_id, updates)}
                  showActionButtons={true}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const LineupTab: React.FC = () => {
  const location = useLocation();
  
  // Extract the actual match ID from the URL path
  const extractMatchIdFromPath = () => {
    const path = location.pathname;
    const match = path.match(/\/score\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})-/);
    return match ? match[1] : '';
  };

  const matchId = extractMatchIdFromPath();
  
  const { matchDetails, loading: matchLoading, error: matchError } = useMatchDetails(matchId);

  const teamAId = matchDetails?.home_team_id;
  const teamBId = matchDetails?.away_team_id;

  // Match status - simplified without timeline integration
  const [matchStatus] = useState<'not_started' | 'live' | 'half_time' | 'full_time'>('not_started');

  const {
    teamASquad,
    teamBSquad,
    teamAFormation,
    teamBFormation,
    loading: lineupLoading,
    error: lineupError,
    updatePlayerLineup,
    updateFormation,
    refetchLineupData,
  } = useLineups(matchId, teamAId, teamBId, matchDetails?.tournament_id);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [pendingScreenshotFiles, setPendingScreenshotFiles] = useState<Record<string, { file: File; objectUrl: string }>>({});

  const PENDING_KEY_SEP = '|';
  const pendingKey = (team: 'A' | 'B', playerId: string) => `${team}${PENDING_KEY_SEP}${playerId}`;

  const handleScreenshotFileSelect = (team: 'A' | 'B', playerId: string, file: File | null) => {
    const key = pendingKey(team, playerId);
    setPendingScreenshotFiles((prev) => {
      const existing = prev[key];
      if (existing) {
        URL.revokeObjectURL(existing.objectUrl);
      }
      if (!file) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: { file, objectUrl: URL.createObjectURL(file) } };
    });
  };

  const getPendingScreenshotUrl = (team: 'A' | 'B', playerId: string): string | null => {
    const entry = pendingScreenshotFiles[pendingKey(team, playerId)];
    return entry?.objectUrl ?? null;
  };

  const handleSave = async () => {
    if (!matchId || !teamAId || !teamBId) {
      setToastMessage('Missing match or team information');
      setToastType('error');
      setShowToast(true);
      return;
    }

    try {
      const uploadedUrls: Record<string, string> = {};
      const pendingEntries = Object.entries(pendingScreenshotFiles);
      for (const [key, { file }] of pendingEntries) {
        const idx = key.indexOf(PENDING_KEY_SEP);
        if (idx === -1) continue;
        const team = key.slice(0, idx) as 'A' | 'B';
        const playerId = key.slice(idx + 1);
        if (team !== 'A' && team !== 'B') continue;
        try {
          const url = await uploadLineupScreenshot(file, matchId, playerId);
          if (url) uploadedUrls[key] = url;
        } catch (err) {
          console.error('Error uploading lineup screenshot:', err);
          setToastMessage('Failed to upload screenshot. Save aborted.');
          setToastType('error');
          setShowToast(true);
          return;
        }
      }

      // Captain selection is optional - no validation required
      // Users can save lineups with or without captains

      // Save match formations first - check if they exist and update, otherwise insert
      if (teamAFormation) {
        // Check if formation already exists for team A
        const { data: existingFormationA, error: checkErrorA } = await supabase
          .from('match_formations')
          .select('id')
          .eq('match_id', matchId)
          .eq('team_id', teamAId)
          .single();

        if (checkErrorA && checkErrorA.code !== 'PGRST116') { // PGRST116 = no rows returned
          throw checkErrorA;
        }

        if (existingFormationA) {
          // Update existing formation
          const { error: updateErrorA } = await supabase
            .from('match_formations')
            .update({
              formation: teamAFormation.formation,
              manager_name: teamAFormation.manager_name,
              updated_at: new Date().toISOString()
            })
            .eq('id', existingFormationA.id);
          
          if (updateErrorA) throw updateErrorA;
        } else {
          // Insert new formation
          const { error: insertErrorA } = await supabase
            .from('match_formations')
            .insert({
              match_id: matchId,
              team_id: teamAId,
              formation: teamAFormation.formation,
              manager_name: teamAFormation.manager_name,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
          
          if (insertErrorA) throw insertErrorA;
        }
      }

      if (teamBFormation) {
        // Check if formation already exists for team B
        const { data: existingFormationB, error: checkErrorB } = await supabase
          .from('match_formations')
          .select('id')
          .eq('match_id', matchId)
          .eq('team_id', teamBId)
          .single();

        if (checkErrorB && checkErrorB.code !== 'PGRST116') { // PGRST116 = no rows returned
          throw checkErrorB;
        }

        if (existingFormationB) {
          // Update existing formation
          const { error: updateErrorB } = await supabase
            .from('match_formations')
            .update({
              formation: teamBFormation.formation,
              manager_name: teamBFormation.manager_name,
              updated_at: new Date().toISOString()
            })
            .eq('id', existingFormationB.id);
          
          if (updateErrorB) throw updateErrorB;
        } else {
          // Insert new formation
          const { error: insertErrorB } = await supabase
            .from('match_formations')
            .insert({
              match_id: matchId,
              team_id: teamBId,
              formation: teamBFormation.formation,
              manager_name: teamBFormation.manager_name,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
          
          if (insertErrorB) throw insertErrorB;
        }
      }

      const screenshotUrlFor = (team: 'A' | 'B', playerId: string, current: string | undefined) =>
        uploadedUrls[pendingKey(team, playerId)] ?? current ?? null;

      // Prepare lineup data for both teams
      const lineupData = [
        // Team A players
        ...teamASquad.map(player => ({
          match_id: matchId,
          team_id: teamAId,
          player_id: player.player_id,
          is_captain: player.isCaptain,
          is_starting: player.isStarting,
          is_bench: player.isBench,
          position: player.position as 'Goalkeeper' | 'Defender' | 'Midfielder' | 'Forward',
          kit_number: player.kit_number,
          identification_description: player.identification_description || null,
          identification_screenshot_url: screenshotUrlFor('A', player.player_id, player.identification_screenshot_url),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })),
        // Team B players
        ...teamBSquad.map(player => ({
          match_id: matchId,
          team_id: teamBId,
          player_id: player.player_id,
          is_captain: player.isCaptain,
          is_starting: player.isStarting,
          is_bench: player.isBench,
          position: player.position as 'Goalkeeper' | 'Defender' | 'Midfielder' | 'Forward',
          kit_number: player.kit_number,
          identification_description: player.identification_description || null,
          identification_screenshot_url: screenshotUrlFor('B', player.player_id, player.identification_screenshot_url),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }))
      ];

      // Check if lineup data already exists for this match
      const { data: existingLineup, error: checkError } = await supabase
        .from('match_lineup')
        .select('id')
        .eq('match_id', matchId);

      if (checkError) throw checkError;

      if (existingLineup && existingLineup.length > 0) {
        // Update existing lineup data
        const { error: deleteError } = await supabase
          .from('match_lineup')
          .delete()
          .eq('match_id', matchId);

        if (deleteError) throw deleteError;
      }

      // Insert new lineup data
      const { error: lineupError } = await supabase
        .from('match_lineup')
        .insert(lineupData);

      if (lineupError) throw lineupError;

      Object.values(pendingScreenshotFiles).forEach(({ objectUrl }) => URL.revokeObjectURL(objectUrl));
      setPendingScreenshotFiles({});

      await refetchLineupData();

      setToastMessage('Lineup saved successfully!');
      setToastType('success');
      setShowToast(true);
      
    } catch (error) {
      console.error('Error saving lineup:', error);
      setToastMessage(error instanceof Error ? error.message : 'Failed to save lineup');
      setToastType('error');
      setShowToast(true);
    }
  };

  // Expose the save function to parent component
  useEffect(() => {
    // Replace the parent's save function with our lineup save logic
    (window as any).lineupSaveHandler = handleSave;
  }, [handleSave]);

  if (matchLoading || lineupLoading) {
    return <TabLoader message="Loading lineup data..." />;
  }

  if (matchError || lineupError) {
    return <div className="text-red-500 p-4">Error: {matchError || lineupError}</div>;
  }

  if (!matchId || !matchDetails || !teamAId || !teamBId) {
    return <div className="text-red-500 p-4">Error: Missing required data</div>;
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-col lg:flex-row gap-4">
        <LineupTeamPanel
          teamName={matchDetails?.home_team?.name || 'Team A'}
          teamType="A"
          teamId={teamAId || ''}
          teamLogo={matchDetails?.home_team?.logo}
          squad={teamASquad}
          formation={teamAFormation}
          matchStatus={matchStatus}
          onPlayerUpdate={(playerId, updates) => updatePlayerLineup('A', playerId, updates)}
          onScreenshotFileSelect={(playerId, file) => handleScreenshotFileSelect('A', playerId, file)}
          getPendingScreenshotUrl={(playerId) => getPendingScreenshotUrl('A', playerId)}
          onFormationUpdate={(formation, managerName) => updateFormation('A', formation, managerName)}
        />
        <LineupTeamPanel
          teamName={matchDetails?.away_team?.name || 'Team B'}
          teamType="B"
          teamId={teamBId || ''}
          teamLogo={matchDetails?.away_team?.logo}
          squad={teamBSquad}
          formation={teamBFormation}
          matchStatus={matchStatus}
          onPlayerUpdate={(playerId, updates) => updatePlayerLineup('B', playerId, updates)}
          onScreenshotFileSelect={(playerId, file) => handleScreenshotFileSelect('B', playerId, file)}
          getPendingScreenshotUrl={(playerId) => getPendingScreenshotUrl('B', playerId)}
          onFormationUpdate={(formation, managerName) => updateFormation('B', formation, managerName)}
        />
      </div>
      
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  );
};

export default LineupTab;